import { T, Var } from 'gt-react';
import { ArrowSquareOutIcon, CircleNotchIcon } from '@phosphor-icons/react';
import { isEmailBodyDocument } from '@erp/shared-utils';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { DocumentViewer } from '@/components/common/DocumentViewer';
import { useDocumentPreview } from '@/api/rpc/hooks/core/useDocuments';

interface Props {
  documentId: string | null;
  onClose: () => void;
}

export function DocumentDetailSheet({ documentId, onClose }: Props) {
  const isOpen = !!documentId;
  return (
    <Sheet open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-2xl overflow-hidden flex flex-col p-0"
      >
        {documentId ? (
          <DocumentDetailBody documentId={documentId} />
        ) : (
          <DocumentHeader title={null} />
        )}
      </SheetContent>
    </Sheet>
  );
}

function DocumentDetailBody({ documentId }: { documentId: string }) {
  const { data, isLoading, error } = useDocumentPreview(documentId);

  if (isLoading) {
    return (
      <>
        <DocumentHeader title={null} />
        <div className="flex-1 flex items-center justify-center">
          <CircleNotchIcon className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </>
    );
  }

  if (error || !data) {
    return (
      <>
        <DocumentHeader title={null} />
        <div className="flex-1 flex items-center justify-center text-muted-foreground">
          <T context="document not found in slidein">Document not found</T>
        </div>
      </>
    );
  }

  const { fileName, mimeType, signedUrl } = data;

  // Docified email bodies render as an email (handled by DocumentViewer), so
  // suppress the raw filename title and the file chrome — there's no useful
  // "open the HTML in a new tab" affordance for a docified body.
  const isEmailBody = isEmailBodyDocument(fileName);

  return (
    <>
      <DocumentHeader
        title={isEmailBody ? null : fileName}
        fallback={
          isEmailBody ? (
            <T context="sheet title, docified email body shown as email">
              Email
            </T>
          ) : undefined
        }
      />
      {!isEmailBody && (
        <div className="px-4 py-2 border-b flex items-center gap-3 text-xs text-muted-foreground">
          {mimeType && <span>{mimeType}</span>}
          {signedUrl && (
            <Button
              variant="ghost"
              size="sm"
              className="ml-auto h-7"
              onClick={() =>
                window.open(signedUrl, '_blank', 'noopener,noreferrer')
              }
            >
              <ArrowSquareOutIcon className="h-3.5 w-3.5 mr-1" />
              <T context="document action, open in new tab">Open in new tab</T>
            </Button>
          )}
        </div>
      )}
      <div className="flex-1 min-h-0 overflow-hidden">
        <DocumentViewer documentId={documentId} />
      </div>
    </>
  );
}

function DocumentHeader({
  title,
  fallback,
}: {
  title: string | null;
  /** Rendered when there's no `title`. Defaults to "Document". */
  fallback?: React.ReactNode;
}) {
  return (
    <SheetHeader className="px-4 py-3 border-b">
      <SheetTitle className="text-sm truncate">
        {title ? (
          <Var>{title}</Var>
        ) : (
          (fallback ?? (
            <T context="sheet title, document detail fallback">Document</T>
          ))
        )}
      </SheetTitle>
    </SheetHeader>
  );
}
